import type { IPublicClientApplication } from '@azure/msal-browser';
import { apiTokenRequest } from './msalConfig';
import { needsInteraction } from './needsInteraction';
import { isNotAssignedError } from './entitlement';
import { isAuthRedirectInFlight, startReauth } from './authSession';

/**
 * Bug #24685 — check the session when the operator comes back to the tab.
 *
 * After hours away the refresh token is often gone, and the first sign of it used to be whatever
 * random API call happened next failing mid-page. Checking once on return means the operator is
 * re-authenticated straight away, back to the same page, before they start working.
 */

/** How long the tab must have been hidden before a return triggers a session check. */
export const RESUME_CHECK_AFTER_HIDDEN_MS = 10 * 60 * 1000;

export type ResumeMsal = Pick<
  IPublicClientApplication,
  'acquireTokenSilent' | 'acquireTokenRedirect' | 'getActiveAccount' | 'getAllAccounts'
>;

/**
 * One silent token request for the signed-in account. A failure only an interactive sign-in can
 * fix starts the single-flight re-auth; anything else (network, a 5xx, AADSTS50105 — an answer, not
 * an expiry) is left for the normal paths to handle. Never rejects.
 */
export async function checkSessionOnResume(msal: ResumeMsal): Promise<void> {
  if (isAuthRedirectInFlight()) return;
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null;
  if (!account) return;
  try {
    await msal.acquireTokenSilent({ ...apiTokenRequest, account });
  } catch (error) {
    if (isNotAssignedError(error) || !needsInteraction(error)) return;
    startReauth(msal, account, apiTokenRequest);
  }
}

/**
 * Registers the `visibilitychange` listener. Call ONCE, after MSAL has initialised and settled any
 * redirect (main.tsx) — not from a hook, or every mounted component would register its own.
 * Returns the unregister function.
 */
export function installSessionResumeCheck(
  msal: ResumeMsal,
  doc: Document = document,
  now: () => number = () => Date.now(),
): () => void {
  let hiddenAt: number | null = doc.visibilityState === 'hidden' ? now() : null;

  const onVisibilityChange = () => {
    if (doc.visibilityState === 'hidden') {
      hiddenAt = now();
      return;
    }
    if (hiddenAt === null) return;
    const hiddenFor = now() - hiddenAt;
    hiddenAt = null;
    if (hiddenFor < RESUME_CHECK_AFTER_HIDDEN_MS) return;
    void checkSessionOnResume(msal);
  };

  doc.addEventListener('visibilitychange', onVisibilityChange);
  return () => doc.removeEventListener('visibilitychange', onVisibilityChange);
}
